import { useEffect, useState } from "react";

import Spinner from "common/components/Spinner";
import { parseNumber } from "common/utils/numbers";

import TextButton from "client/common/components/buttons/TextButton";
import Modal from "client/common/components/deprecated-modals/Modal";
import VerificationInput from "client/common/components/inputs/VerificationInput";
import Alert from "client/common/components/content/Alert";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";

import { useSignUpMutation, useSendVerificationCodeMutation } from "../../hooks";
import { SignupFormValues } from "../../types";

import styles from "../../styles/VerifyEmailSignupModal.module.less";

const CODE_LENGTH = 6;

type VerifyEmailSignupModalProps = {
  visible: boolean;
  onClose: () => void;
  next?: string;
  values: SignupFormValues;
  onTryDifferentEmail: () => void;
  onSendToPhone: () => void;
  /** Hide the "try a different email" option when the email (username) is locked on file. */
  lockUsername: boolean;
  /** Hide the "send to phone instead" option when the user is pinned to the email channel. */
  canSwitchChannel: boolean;
};

const VerifyEmailSignupModal = (props: VerifyEmailSignupModalProps) => {
  const {
    visible,
    onClose,
    values,
    next,
    onTryDifferentEmail,
    onSendToPhone,
    lockUsername,
    canSwitchChannel,
  } = props;
  const { email } = values;

  const [code, setCode] = useState("");

  const {
    mutate: signUp,
    isLoading: isSigningUp,
    error: signUpError,
    reset: resetSignup,
  } = useSignUpMutation("email", next);

  const {
    mutate: sendCode,
    isSuccess: hasSentCode,
    isLoading: isSendingCode,
    error: sendingCodeError,
    reset: resetEmailCode,
  } = useSendVerificationCodeMutation({});

  const error = signUpError || sendingCodeError;
  const nonFieldErrors = error?.errors?.non_field_errors;
  const errorDisplay =
    nonFieldErrors && nonFieldErrors.length > 0 ? nonFieldErrors[0] : error?.detail;

  useEffect(() => {
    // Since this component mounts in the background
    // reset values when it becomes visible
    if (visible) {
      resetSignup();
      resetEmailCode();
      setCode("");
    }
  }, [resetSignup, resetEmailCode, visible]);

  const onSubmit = () => {
    signUp({
      number: parseNumber(values.phone, 0),
      email,
      code,
      profile: {
        first_name: values.first_name,
        last_name: values.last_name,
        new_password: values.password,
      },
    });
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalLayout.Header
        title="We emailed you"
        description={
          <>
            Please enter verification code sent to <strong>{email}</strong>
          </>
        }
        onClose={onClose}
      />
      <ModalLayout.Form
        canSubmit={code.length === CODE_LENGTH}
        onSubmit={onSubmit}
        isLoading={isSigningUp}
        submitText="Submit"
      >
        {hasSentCode && <Alert variant="success">Code resent to {email}</Alert>}
        {errorDisplay && <Alert variant="danger">{errorDisplay}</Alert>}
        <div className={styles.input}>
          <VerificationInput value={code} onChange={setCode} size={CODE_LENGTH} />
        </div>
        {isSendingCode ? (
          <div className={styles.sending}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.resend}>
            <div className={styles.helperText}>
              Didn&apos;t get an email?
              <br />
              Make sure the email is valid.
            </div>
            <div className={styles.optionsList}>
              <div className={styles.option}>
                <TextButton
                  type="button"
                  disabled={isSigningUp || isSendingCode}
                  onClick={() => sendCode({ email })}
                  icon="right"
                  hasUnderline={true}
                >
                  Resend code
                </TextButton>
              </div>
              {!lockUsername && (
                <div className={styles.option}>
                  <TextButton
                    type="button"
                    disabled={isSigningUp || isSendingCode}
                    onClick={() => {
                      onTryDifferentEmail();
                      onClose();
                    }}
                    icon="right"
                    hasUnderline={true}
                  >
                    Try a different email
                  </TextButton>
                </div>
              )}
              {canSwitchChannel && (
                <div className={styles.option}>
                  <TextButton
                    type="button"
                    disabled={isSigningUp || isSendingCode}
                    onClick={() => {
                      onSendToPhone();
                      onClose();
                    }}
                    icon="right"
                    hasUnderline={true}
                  >
                    Send to phone number instead
                  </TextButton>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalLayout.Form>
    </Modal>
  );
};

export default VerifyEmailSignupModal;
